import React from "react";
import { Flex, Text, Card, TextInput, Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { toDbId } from "~/utils/graphql-helpers";
import { ArenaGridRow } from "../../../DataVault/Entities/types";
import { validateEmail } from "../../utils/validation";
import * as styles from "./styles_vanilla.css";

interface ArenaDetailsProps {
  selectedArena: ArenaGridRow;
  selectedEnvironment: string;
  adminEmail: string;
  adminEmailError: string;
  onAdminEmailChange: (email: string) => void;
  onAdminEmailErrorChange: (error: string) => void;
  onChangeArena: () => void;
}

export const ArenaDetails: React.FC<ArenaDetailsProps> = ({
  selectedArena,
  selectedEnvironment,
  adminEmail,
  adminEmailError,
  onAdminEmailChange,
  onAdminEmailErrorChange,
  onChangeArena,
}) => {
  return (
    <Card className={styles.arenaSelectionCard}>
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Flex alignItems="center" gap={Sizes.XS}>
          <Icon type="check-circle" size="medium" color={Colors.Neutral600} />
          <Text variant="h4">Selected Arena</Text>
          <Button
            variant="neutral"
            onClick={onChangeArena}
            data-testid="change-arena-button"
            className={styles.changeButton}
            icon="edit"
          />
        </Flex>

        <div className={styles.arenaInfoGrid}>
          <div>
            <Text variant="strong-lg" className={styles.fieldLabel}>
              Arena Domain Name
            </Text>
            <Text variant="strong-md">{selectedArena.domain}</Text>
          </div>
          {selectedEnvironment === "production" && (
            <div>
              <Text variant="strong-lg" className={styles.fieldLabel}>
                Arena ID
              </Text>
              <Text variant="strong-md">{toDbId(selectedArena.id)}</Text>
            </div>
          )}
          <div>
            <Text variant="strong-lg" className={styles.fieldLabel}>
              Environment
            </Text>
            <div
              className={
                selectedEnvironment === "production"
                  ? styles.environmentBadgeProduction
                  : styles.environmentBadgeTest
              }
            >
              {selectedEnvironment}
            </div>
          </div>
        </div>

        <div className={styles.adminEmailSection}>
          <Flex alignItems="center" gap={Sizes.SM} className={styles.adminEmailHeader}>
            <Icon type="envelope" size="medium" color={Colors.Neutral600} />
            <Text variant="strong-lg" className={styles.fieldLabel}>
              Admin User Email
            </Text>
          </Flex>

          <TextInput
            type="email"
            placeholder="Enter the email address of the user who will execute this import operation"
            value={adminEmail}
            onChange={(e) => {
              const newEmail = e.target.value;
              onAdminEmailChange(newEmail);
              if (adminEmailError && newEmail.trim()) {
                const error = validateEmail(newEmail);
                onAdminEmailErrorChange(error);
              } else if (adminEmailError) {
                onAdminEmailErrorChange("");
              }
            }}
            onBlur={() => {
              const error = validateEmail(adminEmail);
              onAdminEmailErrorChange(error);
            }}
            errorMessage={adminEmailError || undefined}
            isFullWidth
            required
            data-testid="admin-email-input"
          />

          <Text variant="regular-sm" color={Colors.Neutral600} className={styles.helpText}>
            Please ensure the specified user has the necessary permissions to perform this
            operation.
          </Text>
        </div>
      </Flex>
    </Card>
  );
};
